import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { RCButton, RCLightbox } from '@reverbdotcom/cadence/components';
import {
  CircleHalfIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  MoonIcon,
} from '@reverbdotcom/cadence/icons/react';

import isNode from '../is-node';
import experiments from '../experiments';
import { useExpEnabled } from '../user_hooks';
import LightboxImageItem from './lightbox_image_item';
import {
  LightboxImageControls,
  handleArrowKeyDown,
  isLink,
  useWindowArrowKeys,
} from './lightbox_image_parts';
import { LightboxViewerProps } from './lightbox_image_viewer';
import { PinchSource, usePinchGesture } from './lightbox_image_gestures';

// below this, zooming barely changes the photo
const MIN_ZOOM = 1.25;
// the least a zoom enlarges, for a photo already shown near its natural size
const ZOOM_STEP = 2;
// how far past its natural size a photo can be enlarged
const MAX_NATURAL_SCALE = 2;
// how far a pinch can shrink a photo below its fit, and the scale a pinch has to end above to stay zoomed
const MIN_PINCH_SCALE = 0.8;
const SNAP_BACK_SCALE = 1.1;
// how far an arrow key pans a zoomed photo, as a share of the stage
const KEY_PAN_DISTANCE = 0.2;
const KEY_PAN_STEPS: Record<string, { x: number; y: number }> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

// The range along one axis where the scaled photo's start can sit in the stage: panning can bring either of
// its edges `inset` in from the stage's edge, clear of the controls over it. A photo that fits inside the
// inset stays centered. Without an inset, an overflowing photo pans edge to edge.
function panRange(box: number, scaled: number, inset: number) {
  const centered = (box - scaled) / 2;
  return { first: Math.max(inset, centered), last: Math.min(box - scaled - inset, centered) };
}

// where the scaled photo's start sits in the stage along one axis, with the pan (0–1) running through its
// range from showing the photo's start to showing its end
function photoOffset(pan: number, box: number, scaled: number, inset: number) {
  const { first, last } = panRange(box, scaled, inset);
  return first + (pan * (last - first));
}

// the pan along one axis that puts the scaled photo's start at `offset`
function panTo(offset: number, box: number, scaled: number, inset: number) {
  const { first, last } = panRange(box, scaled, inset);
  return first === last ? 0.5 : clamp((offset - first) / (last - first), 0, 1);
}

// the scale a click zooms a photo to, from its contain-fit scale (`fit`): at least its natural size, at
// least a full step, and no more than twice its natural size; 0 when that's not worth zooming
function zoomScale(fit: number) {
  if (!fit) return 0;
  const scale = Math.min(Math.max(1 / fit, ZOOM_STEP), MAX_NATURAL_SCALE / fit);
  return scale >= MIN_ZOOM ? scale : 0;
}

// the transform origin along one axis that maps the pointer's place across the stage (0–1) to the same
// place across the scaled photo, so the stage's edge shows the photo's edge. `fitted` is the photo's
// contain-fit size, centered in the img box. Scaling around an origin `o` moves the box's start by
// `o * (1 - scale)`, so the origin is the offset that start needs, divided by that.
function panOrigin(pointer: number, box: number, fitted: number, scale: number, inset: number) {
  // at the fit, the origin makes no difference
  if (scale === 1) return box / 2;
  const edge = photoOffset(pointer, box, fitted * scale, inset);
  const letterbox = ((box - fitted) / 2) * scale;
  return (edge - letterbox) / (1 - scale);
}

// the pan along one axis that keeps the point of the photo under `focus` there as the scale changes, so a
// pinch zooms around the fingers
function panAround(
  pan: number,
  box: number,
  fitted: number,
  scale: number,
  nextScale: number,
  focus: number,
  inset: number,
) {
  const scaled = fitted * scale;
  const next = fitted * nextScale;
  // the place across the photo under the focus, 0–1
  const spot = (focus - photoOffset(pan, box, scaled, inset)) / scaled;
  return panTo(focus - (spot * next), box, next, inset);
}

// the pan along one axis after moving the photo by `delta` pixels
function panBy(pan: number, box: number, scaled: number, delta: number, inset: number) {
  return panTo(photoOffset(pan, box, scaled, inset) + delta, box, scaled, inset);
}

// Brings a rail's thumbnail into view by scrolling the rail alone, along whichever axis it runs.
// `scrollIntoView` would also scroll the frame around it, which can push the frame's controls off screen
// when it runs mid-layout, such as while the frame animates open or the phone turns.
function scrollRailTo(rail: HTMLElement, index: number) {
  const thumb = rail.children[index];
  if (!thumb) return;

  const railRect = rail.getBoundingClientRect();
  const thumbRect = thumb.getBoundingClientRect();
  const left = thumbRect.left - railRect.left;
  const top = thumbRect.top - railRect.top;

  let { scrollLeft, scrollTop } = rail;
  if (left < 0) scrollLeft += left;
  else if (left + thumbRect.width > rail.clientWidth) scrollLeft += left + thumbRect.width - rail.clientWidth;
  if (top < 0) scrollTop += top;
  else if (top + thumbRect.height > rail.clientHeight) scrollTop += top + thumbRect.height - rail.clientHeight;

  rail.scrollTo?.({ left: scrollLeft, top: scrollTop });
}

// The arrows are hidden at the first and last item. If the one going away has focus, focus moves to the
// dialog, so it isn't lost to the page, and so pressing Enter or Space again does nothing.
function keepFocusAtEnd(newIndex: number, count: number) {
  let edge: 'next' | 'prev' | null = null;
  if (newIndex === count - 1) edge = 'next';
  else if (newIndex === 0) edge = 'prev';
  if (!edge) return;

  const arrow = document.activeElement?.closest(`.lightbox-image__control-wrapper--${edge}`);
  arrow?.closest<HTMLElement>('[role="dialog"]')?.focus();
}

// TRIAL: for people who can choose the site's theme (the web_theme_control rollout), a button switches
// the lightbox between following that theme and always dark, so they can compare the two. The choice is
// remembered per browser. Remove it once the stage's color is decided.
const THEME_TRIAL_KEY = 'lightboxThemeTrial';
type TrialTheme = 'inherit' | 'dark';

function readTrialTheme(): TrialTheme {
  if (isNode) return 'inherit';
  try {
    return window.localStorage.getItem(THEME_TRIAL_KEY) === 'dark' ? 'dark' : 'inherit';
  } catch {
    return 'inherit';
  }
}

function saveTrialTheme(theme: TrialTheme) {
  try {
    window.localStorage.setItem(THEME_TRIAL_KEY, theme);
  } catch {
    // the choice just isn't remembered
  }
}

interface Props extends LightboxViewerProps {
  id?: string;
  caption?: string;
  disableImageOverlay?: boolean;
}

// A full-screen viewer whose stage fills the frame, beside a rail of thumbnails.
export default function LightboxImageStageViewer({
  items,
  index,
  onIndexChange,
  isOpen,
  onOpenChange,
  label,
  id,
  caption,
  disableImageOverlay,
}: Props) {
  const [displayVideo, setDisplayVideo] = React.useState(false);
  // the full-size images that have loaded, by URL, so a new list of items doesn't inherit them by position
  const [loaded, setLoaded] = React.useState<string[]>([]);
  const [wasOpen, setWasOpen] = React.useState(isOpen);
  // held in state, not refs: the rail and track mount a render after opening, once the frame's portal is ready
  const [rail, setRail] = React.useState<HTMLDivElement | null>(null);
  const [track, setTrack] = React.useState<HTMLDivElement | null>(null);
  // the slide the track is scrolling itself to, so the slides it passes on the way don't change the index
  const scrollTarget = React.useRef<number | null>(null);
  // whether the active slide animates in: it does when a control brought it, not a scroll
  const [entering, setEntering] = React.useState(false);
  // the scale while zoomed, 0 otherwise
  const [zoom, setZoom] = React.useState(0);
  // whether the active photo is worth zooming, which shows the zoom button and a mouse's zoom cursor
  const [zoomable, setZoomable] = React.useState(false);
  const showThemeTrial = useExpEnabled(experiments.WEB_THEME_CONTROL);
  const [trialTheme, setTrialTheme] = React.useState(readTrialTheme);
  // While zoomed, the scale and how far the photo is panned across each axis (0–1). Held in a ref and
  // written to the stage's style, as it changes with every move of a pointer or fingers.
  const view = React.useRef({ scale: 1, x: 0.5, y: 0.5 });
  // a pinch follows the fingers directly, without the eased transition a click zoom has
  const [pinching, setPinching] = React.useState(false);
  const pinchStartScale = React.useRef(1);
  const [stage, setStage] = React.useState<HTMLDivElement | null>(null);
  const controlsInset = React.useRef<HTMLSpanElement>(null);

  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    setDisplayVideo(false);
    setZoom(0);
  }

  // While zoomed, the arrow keys pan and Escape unzooms, rather than moving between photos and closing:
  // a capturing listener on window runs ahead of the viewer's and the frame's own.
  const zoomedKeyDown = React.useRef<(e: KeyboardEvent) => void>(() => {});
  zoomedKeyDown.current = handleZoomedKeyDown;

  React.useEffect(() => {
    if (!zoom) return undefined;

    const listener = (e: KeyboardEvent) => zoomedKeyDown.current(e);
    window.addEventListener('keydown', listener, true);
    return () => window.removeEventListener('keydown', listener, true);
  }, [zoom]);

  const isSingleItem = items.length === 1;
  const showRail = !isSingleItem && items.every(item => item.thumbnail);
  const isFirst = index === 0;
  const isLast = index === items.length - 1;

  React.useEffect(() => {
    if (rail) scrollRailTo(rail, index);
  }, [rail, index]);

  // Brings the track straight to the index when something other than scrolling changed it, where the slide
  // comes into focus: only a finger or trackpad moves the photos across.
  React.useEffect(() => {
    if (!track?.clientWidth) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    if (current === index) return;

    scrollTarget.current = index;
    track.scrollTo?.({ left: index * track.clientWidth, behavior: 'auto' });
  }, [track, index]);

  // Keeps the track on the index whenever it's resized, which includes opening: the frame mounts hidden,
  // with no width to scroll by, and is sized a moment later.
  const indexRef = React.useRef(index);
  indexRef.current = index;
  // the width the track was last lined up at: a resize scrolls it too, which isn't a swipe
  const trackWidth = React.useRef(0);

  React.useEffect(() => {
    if (!track || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      trackWidth.current = track.clientWidth;
      const left = indexRef.current * track.clientWidth;
      if (!track.clientWidth || track.scrollLeft === left) return;
      scrollTarget.current = indexRef.current;
      track.scrollTo?.({ left, behavior: 'auto' });
    });

    observer.observe(track);
    return () => observer.disconnect();
  }, [track]);

  // Whether the active photo is worth zooming depends on its full-size image and the stage's size, so it's
  // checked as each loads, and whenever the stage is resized.
  const checkZoomable = React.useRef(updateZoomable);
  checkZoomable.current = updateZoomable;

  React.useEffect(() => {
    checkZoomable.current();
  }, [index, loaded, track]);

  React.useEffect(() => {
    if (!stage || typeof ResizeObserver === 'undefined') return undefined;

    let size = '';
    const observer = new ResizeObserver(() => {
      // a zoomed photo's pan is laid out for the stage's old size, so a new one zooms back out
      const nextSize = `${stage.clientWidth}x${stage.clientHeight}`;
      if (size && nextSize !== size) setZoom(0);
      size = nextSize;
      checkZoomable.current();
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, [stage]);

  // The same for the rail's active thumbnail, which can't be scrolled to while the frame is hidden either.
  React.useEffect(() => {
    if (!rail || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      if (rail.clientWidth) scrollRailTo(rail, indexRef.current);
    });

    observer.observe(rail);
    return () => observer.disconnect();
  }, [rail]);

  function goTo(newIndex: number, source: 'next' | 'previous' | 'thumbnail', { scrolled = false } = {}) {
    setDisplayVideo(false);
    setZoom(0);
    setEntering(!scrolled);
    onIndexChange(newIndex, { source });
  }

  // The index follows the slide that fills most of the track, so the count changes halfway through a swipe.
  function handleTrackScroll() {
    // a track that's been resized, such as by turning the phone, is lined up again by its resize observer
    if (!track?.clientWidth || track.clientWidth !== trackWidth.current) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);

    if (scrollTarget.current !== null) {
      if (current === scrollTarget.current) scrollTarget.current = null;
      return;
    }

    if (current === index || !items[current]) return;
    goTo(current, current > index ? 'next' : 'previous', { scrolled: true });
  }

  // a finger or pointer on the track takes over from a scroll the track started itself
  function handleTrackPointerDown() {
    scrollTarget.current = null;
  }

  // Stops at the first and last item, like swiping the track.
  // Also keeps a focused track from scrolling itself on the arrow keys.
  function loadNext(e) {
    e.stopPropagation();
    e.preventDefault?.();
    if (isLast) return;
    keepFocusAtEnd(index + 1, items.length);
    goTo(index + 1, 'next');
  }

  function loadPrevious(e) {
    e.stopPropagation();
    e.preventDefault?.();
    if (isFirst) return;
    keepFocusAtEnd(index - 1, items.length);
    goTo(index - 1, 'previous');
  }

  const arrowKeyHandlers = { onPrevious: loadPrevious, onNext: loadNext };

  useWindowArrowKeys(isOpen && !isSingleItem, arrowKeyHandlers);

  function handleKeyDown(e) {
    if (isSingleItem) return;
    handleArrowKeyDown(e, arrowKeyHandlers);
  }

  function handleImageLoaded(src: string) {
    setLoaded(current => (current.includes(src) ? current : [...current, src]));
  }

  // The full-size image is what zooms, so there's nothing to zoom until it has loaded.
  function stageImage() {
    const src = items[index]?.src;
    if (!isLink(src) || !loaded.includes(src)) return null;
    return track?.children[index]?.querySelector<HTMLImageElement>('.lightbox-image-item img');
  }

  // The stage and the active photo's contain-fit size in it, which every zoom input works from. A zoomed
  // photo fills the whole stage, without the room it leaves for the controls and rail while unzoomed, and
  // pans far enough for any of its edges to clear the controls over the stage's edges (`inset`).
  function zoomGeometry() {
    const img = stageImage();
    const box = stage?.getBoundingClientRect();
    if (!img?.naturalWidth || !box?.width || !box.height) return null;

    const fit = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight);
    return {
      box,
      width: img.naturalWidth * fit,
      height: img.naturalHeight * fit,
      inset: controlsInset.current?.offsetHeight ?? 0,
      maxScale: Math.max(MAX_NATURAL_SCALE / fit, 1),
      clickScale: zoomScale(fit),
    };
  }

  // Writes the view straight to the stage's style, so following a pointer or fingers doesn't re-render.
  // The stage scales the photo by it while it's zoomed. The origin is a share of the stage, so it holds
  // while the photo's box eases out to the stage's edges as the zoom starts.
  function showView() {
    const geometry = zoomGeometry();
    if (!geometry || !stage) return;

    const { box, width, height, inset } = geometry;
    const { scale, x, y } = view.current;
    const originX = (panOrigin(x, box.width, width, scale, inset) / box.width) * 100;
    const originY = (panOrigin(y, box.height, height, scale, inset) / box.height) * 100;
    stage.style.setProperty('--zoom-origin-x', `${originX}%`);
    stage.style.setProperty('--zoom-origin-y', `${originY}%`);
    stage.style.setProperty('--zoom-scale', `${scale}`);
  }

  // a client point's place across the stage, 0–1 on each axis
  function stagePoint(clientX: number, clientY: number) {
    const box = stage?.getBoundingClientRect();
    if (!box?.width || !box.height) return { x: 0.5, y: 0.5 };
    return {
      x: clamp((clientX - box.left) / box.width, 0, 1),
      y: clamp((clientY - box.top) / box.height, 0, 1),
    };
  }

  // zooms to a scale, keeping the photo's point under a client point where it is
  function zoomAround(scale: number, clientX: number, clientY: number) {
    const geometry = zoomGeometry();
    if (!geometry) return;

    const { box, width, height, inset } = geometry;
    const { current } = view;
    view.current = {
      scale,
      x: panAround(current.x, box.width, width, current.scale, scale, clientX - box.left, inset),
      y: panAround(current.y, box.height, height, current.scale, scale, clientY - box.top, inset),
    };
    showView();
  }

  // a mouse pans by pointing: the pointer's place across the stage is the place across the photo in view
  function followPointer(scale: number, clientX: number, clientY: number) {
    view.current = { scale, ...stagePoint(clientX, clientY) };
    showView();
  }

  function canClickZoom() {
    return !isNode && !!window.matchMedia?.('(pointer: fine)').matches;
  }

  function updateZoomable() {
    setZoomable(!!zoomGeometry()?.clickScale);
  }

  function handlePointerMove(evt: React.PointerEvent) {
    // touches pan and pinch through the gesture handlers
    if (evt.pointerType === 'touch' || !zoom) return;
    followPointer(view.current.scale, evt.clientX, evt.clientY);
  }

  function toggleTrialTheme() {
    const next = trialTheme === 'dark' ? 'inherit' : 'dark';
    setTrialTheme(next);
    saveTrialTheme(next);
  }

  // the zoom button zooms into the photo's center, and back out
  function toggleZoom() {
    if (zoom) {
      setZoom(0);
      return;
    }

    const scale = zoomGeometry()?.clickScale;
    if (!scale) return;

    view.current = { scale, x: 0.5, y: 0.5 };
    showView();
    setZoom(scale);
  }

  function handleZoomedKeyDown(e: KeyboardEvent) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key === 'Escape') {
      e.stopPropagation();
      setZoom(0);
      return;
    }

    const step = KEY_PAN_STEPS[e.key];
    const geometry = zoomGeometry();
    if (!step || !geometry) return;
    e.stopPropagation();
    e.preventDefault();

    // a key shows more of the photo in its direction, so the photo moves the other way
    const { box, width, height, inset } = geometry;
    const { scale, x, y } = view.current;
    view.current = {
      scale,
      x: panBy(x, box.width, width * scale, -step.x * box.width * KEY_PAN_DISTANCE, inset),
      y: panBy(y, box.height, height * scale, -step.y * box.height * KEY_PAN_DISTANCE, inset),
    };
    showView();
  }

  function handleImageClick(evt) {
    evt.stopPropagation();

    if (!isLink(items[index].src)) {
      setDisplayVideo(true);
      return;
    }

    // a tap doesn't zoom: fingers double tap, and a double tap's second tap also clicks
    if (!canClickZoom()) return;

    if (zoom) {
      setZoom(0);
      return;
    }

    const scale = zoomGeometry()?.clickScale;
    if (!scale) return;

    followPointer(scale, evt.clientX, evt.clientY);
    setZoom(scale);
  }

  // Pinches zoom continuously between a little under the fit and twice the photo's natural size; one
  // finger pans a zoomed photo, and a double tap zooms in or back out. Fingers zoom around their midpoint;
  // a trackpad zooms around the pointer, which then pans by pointing like a mouse.
  usePinchGesture(stage, {
    onPinchStart() {
      if (!zoomGeometry()) return false;
      if (!zoom) view.current = { scale: 1, x: 0.5, y: 0.5 };
      pinchStartScale.current = view.current.scale;
      showView();
      setPinching(true);
      setZoom(view.current.scale);
      return true;
    },
    onPinch(ratio: number, clientX: number, clientY: number, source: PinchSource) {
      const geometry = zoomGeometry();
      if (!geometry) return;

      const scale = clamp(pinchStartScale.current * ratio, MIN_PINCH_SCALE, geometry.maxScale);
      if (source === 'trackpad') {
        followPointer(scale, clientX, clientY);
      } else {
        zoomAround(scale, clientX, clientY);
      }
    },
    onPinchEnd() {
      setPinching(false);
      setZoom(view.current.scale < SNAP_BACK_SCALE ? 0 : view.current.scale);
    },
    onPan(dx: number, dy: number) {
      const geometry = zoomGeometry();
      if (!zoom || !geometry) return;

      const { box, width, height, inset } = geometry;
      const { scale, x, y } = view.current;
      view.current = {
        scale,
        x: panBy(x, box.width, width * scale, dx, inset),
        y: panBy(y, box.height, height * scale, dy, inset),
      };
      showView();
    },
    onDoubleTap(clientX: number, clientY: number) {
      if (zoom) {
        setZoom(0);
        return;
      }

      const scale = zoomGeometry()?.clickScale;
      if (!scale) return;

      view.current = { scale: 1, x: 0.5, y: 0.5 };
      zoomAround(scale, clientX, clientY);
      setZoom(scale);
    },
  });

  return (
    <>
      {!isNode &&
        <RCLightbox
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          label={label}
          theme={showThemeTrial ? trialTheme : 'inherit'}
          background="recessed"
          closeOnSwipeDown
        >
          <div
            id={id}
            className={classNames(
              'lightbox-stage-viewer',
              { 'lightbox-stage-viewer--with-rail': showRail },
              { 'lightbox-stage-viewer--zoomed': !!zoom },
            )}
          >
            <div className="lightbox-stage-viewer__stage-area">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                className={classNames(
                  'lightbox-stage-viewer__stage',
                  { 'lightbox-stage-viewer__stage--playing': displayVideo },
                  { 'lightbox-stage-viewer__stage--zoomable': zoomable && !zoom && canClickZoom() },
                  { 'lightbox-stage-viewer__stage--zoomed': !!zoom },
                  { 'lightbox-stage-viewer__stage--pinching': pinching },
                )}
                onKeyDown={handleKeyDown}
                onPointerMove={handlePointerMove}
                ref={setStage}
              >
                <span className="lightbox-stage-viewer__controls-inset" ref={controlsInset} aria-hidden />
                {/* while zoomed, one finger pans the photo instead of swiping it away */}
                <RCLightbox.SwipeTarget className="lightbox-stage-viewer__swipe-target" disabled={!!zoom}>
                  <div
                    className="lightbox-stage-viewer__track"
                    ref={setTrack}
                    onScroll={handleTrackScroll}
                    onPointerDown={handleTrackPointerDown}
                  >
                    {/*
                      Every slide shows its preview straight away, and the full-size image loads for the active
                      slide and its neighbors, ready for a swipe. Once loaded, a full-size image stays and its
                      preview goes, so a slide never holds both. Both load eagerly: an off-screen slide's image
                      wouldn't load lazily until scrolled near, so a jump to it would be blank. A video has no
                      preview, so its poster loads straight away instead; the player itself waits for a click.
                    */}
                    {items.map(({ src, preview, alt }, idx) => {
                      const isLoaded = loaded.includes(src);
                      const showPreview = isLink(preview) && !isLoaded;
                      const isVideo = !isLink(src);

                      return (
                        <div
                          key={idx}
                          className={classNames(
                            'lightbox-stage-viewer__slide',
                            { 'lightbox-stage-viewer__slide--active': idx === index },
                            { 'lightbox-stage-viewer__slide--entering': entering && idx === index },
                            { 'lightbox-stage-viewer__slide--loading': !isLoaded && !showPreview },
                          )}
                        >
                          {showPreview &&
                            <img
                              className="lightbox-stage-viewer__preview"
                              src={preview}
                              alt=""
                              loading="eager"
                            />
                          }
                          {(isLoaded || isVideo || Math.abs(idx - index) <= 1) &&
                            <LightboxImageItem
                              urlOrSlug={src}
                              alt={alt}
                              onLoad={() => handleImageLoaded(src)}
                              onClick={handleImageClick}
                              renderVideo={displayVideo && idx === index}
                              index={idx}
                              eagerLoad
                              disableImageOverlay
                              contain
                            />
                          }
                        </div>
                      );
                    })}
                  </div>
                </RCLightbox.SwipeTarget>
                {!isSingleItem &&
                  <LightboxImageControls
                    size="medium"
                    onPrevious={isFirst ? undefined : loadPrevious}
                    onNext={isLast ? undefined : loadNext}
                  />
                }
                {(!isSingleItem || !!caption?.length) &&
                  <div className="lightbox-stage-viewer__overlay">
                    <div className="lightbox-stage-viewer__overlay-pill">
                      {!isSingleItem &&
                        <div className="lightbox-stage-viewer__position" aria-live="polite">
                          {I18n.t('commons.lightboxImage.position', { position: index + 1, count: items.length })}
                        </div>
                      }
                      {!!caption?.length &&
                        <div className="lightbox-stage-viewer__caption">
                          {caption}
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
            {/* outside the stage, so pointing at it doesn't pan the zoomed photo */}
            {(showThemeTrial || zoomable || !!zoom) &&
              <div className="lightbox-stage-viewer__actions">
                {showThemeTrial &&
                  <RCButton
                    variant="elevated-light"
                    size="medium"
                    iconOnly={trialTheme === 'dark' ? <CircleHalfIcon weight="fill" /> : <MoonIcon />}
                    iconLabel={trialTheme === 'dark' ? 'Follow the page theme' : 'Use a dark lightbox'}
                    onClick={toggleTrialTheme}
                  />
                }
                {(zoomable || !!zoom) &&
                  <RCButton
                    variant="elevated-light"
                    size="medium"
                    iconOnly={zoom ? <MagnifyingGlassMinusIcon /> : <MagnifyingGlassPlusIcon />}
                    iconLabel={I18n.t(zoom ? 'commons.lightboxImage.zoomOut' : 'commons.lightboxImage.zoomIn')}
                    onClick={toggleZoom}
                  />
                }
              </div>
            }
            {showRail &&
              <div className="lightbox-stage-viewer__rail" ref={setRail}>
                {items.map(({ thumbnail, alt }, idx) => (
                  <div
                    key={idx}
                    className={classNames(
                      'lightbox-image__thumb',
                      { 'lightbox-image__thumb--active': idx === index },
                    )}
                  >
                    <LightboxImageItem
                      urlOrSlug={thumbnail}
                      index={idx}
                      alt={alt}
                      onClick={(evt) => {
                        evt.preventDefault();
                        goTo(idx, 'thumbnail');
                      }}
                      disableImageOverlay={disableImageOverlay}
                      thumbnail
                      contain
                    />
                  </div>
                ))}
              </div>
            }
          </div>
        </RCLightbox>
      }
    </>
  );
}
